import type { ReactNode, RefObject } from 'react';
import WorkspaceSpotlightTour, {
  type WorkspaceSpotlightTourStep,
} from '../../../shared/ui/common/WorkspaceSpotlightTour';
import type { UseExportRunLogReturn } from '../hooks/useExportRunLog';
import type { UseExportStateReturn } from '../hooks/useExportState';
import type { useExportWorkspaceCommands } from '../hooks/useExportWorkspaceCommands';
import { useExportAvailableParameters } from '../hooks/useExportAvailableParameters';
import ExportGuideDialog from './ExportGuideDialog';
import ExportOutputPathDialog from './ExportOutputPathDialog';
import ExportProgressRequest from './ExportProgressRequest';
import ExportRunLogWindow from './ExportRunLogWindow';
import ExportSettingsDialog from './ExportSettingsDialog';

interface ExportWorkspaceOverlaysProps {
  exportState: UseExportStateReturn;
  exportLog: UseExportRunLogReturn;
  commands: ReturnType<typeof useExportWorkspaceCommands>;
  exportBusy: boolean;
  selectedCount: number;
  effectiveSettingsOpen: boolean;
  settingsPreviewOnly: boolean;
  guideOpen: boolean;
  tourOpen: boolean;
  tourStep: number;
  tourSteps: readonly WorkspaceSpotlightTourStep[];
  unsavedSettingsDialog: ReactNode;
  exportSettingsDialogRef: RefObject<HTMLDivElement | null>;
  settingsGroupingRef: RefObject<HTMLElement | null>;
  settingsOutputPathRef: RefObject<HTMLElement | null>;
  settingsValidationRef: RefObject<HTMLElement | null>;
  onCloseSettings: () => void;
  onCloseGuide: () => void;
  onOpenTour: () => void;
  onCloseTour: () => void;
  onSetTourStep: (step: number) => void;
}

export default function ExportWorkspaceOverlays(props: ExportWorkspaceOverlaysProps) {
  const availableParameters = useExportAvailableParameters({
    open: props.effectiveSettingsOpen,
    systems: props.exportState.systems,
  });

  return (
    <>
      <ExportProgressRequest
        open={props.exportBusy}
        pickBusy={props.commands.pickExportBusy}
        validating={props.commands.validationPreflightBusy}
        pickStep={props.commands.pickExportStep}
        pickProgress={props.commands.pickExportProgress}
        selectedCount={props.selectedCount}
        exportState={props.exportState}
      />
      <ExportSettingsDialog
        open={props.effectiveSettingsOpen}
        onClose={props.onCloseSettings}
        config={props.exportState.config}
        onConfigChange={props.exportState.setConfig}
        onBrowseOutputPath={props.commands.handleBrowseOutputPath}
        previewOnly={props.settingsPreviewOnly}
        contentRef={props.exportSettingsDialogRef}
        groupingRef={props.settingsGroupingRef}
        outputPathRef={props.settingsOutputPathRef}
        validationRef={props.settingsValidationRef}
        availableParameters={availableParameters}
      />
      <ExportOutputPathDialog
        state={props.commands.pathDialogState}
        setState={props.commands.setPathDialogState}
        onClose={props.commands.handleClosePathDialog}
        onConfirm={props.commands.handleConfirmPathDialog}
      />
      <ExportGuideDialog
        open={props.guideOpen}
        onClose={props.onCloseGuide}
        onStartTour={props.onOpenTour}
        systemCount={props.exportState.systems.length}
        selectedCount={props.selectedCount}
        outputPath={props.exportState.config.outputPath || ''}
        loading={props.exportState.loading}
      />
      <ExportRunLogWindow
        open={props.exportLog.windowOpen}
        log={props.exportLog.log}
        onClose={props.exportLog.closeWindow}
      />
      {props.unsavedSettingsDialog}
      <WorkspaceSpotlightTour
        open={props.tourOpen}
        steps={props.tourSteps}
        stepIndex={props.tourStep}
        onBack={() => props.onSetTourStep(Math.max(0, props.tourStep - 1))}
        onNext={() => props.onSetTourStep(Math.min(props.tourSteps.length - 1, props.tourStep + 1))}
        onJumpToStep={props.onSetTourStep}
        onClose={props.onCloseTour}
        dialogLabel="PCF export workspace tour"
        showArrows
      />
    </>
  );
}
